import React from 'react'
import styled, { css } from 'styled-components'
import { __DEV__ } from '@mote/helpers/constants'
import { VisuallyHidden } from '../visuallyHidden/visuallyHidden'
import { useRadioContext } from './radio.context'

const SIZES = {
  sm: { radio: 12, font: 14 },
  md: { radio: 16, font: 15 },
  lg: { radio: 20, font: 15 },
  xl: { radio: 24, font: 15 }
}

export const RadioButton = ({
  checked,
  defaultChecked,
  onChange,
  label,
  size: _size = 'xl',
  labelPosition = 'right',
  disabled = false,
  invalid = false,
  value: radioValue,
  ...rest
}) => {
  const [isChecked, setIsChecked] = React.useState(!!defaultChecked)
  const hasLabel = React.useMemo(() => Boolean(label), [label])
  const {
    value: groupValue,
    disabledAll,
    inGroup,
    size: groupSize,
    updateState
  } = useRadioContext()
  const isDisabled = React.useMemo(
    () => disabled || disabledAll,
    [disabled, disabledAll]
  )
  const size = React.useMemo(
    () => (inGroup ? groupSize : _size),
    [inGroup, groupSize, _size]
  )

  React.useEffect(() => {
    if (!inGroup) return

    setIsChecked(groupValue === radioValue)
  }, [groupValue, inGroup, radioValue])

  React.useEffect(() => {
    if (checked === undefined) return
    setIsChecked(Boolean(checked))
  }, [checked])

  function handleOnChange(event) {
    if (isDisabled || (inGroup && isChecked)) return
    const customEvent = {
      target: {
        checked: !isChecked,
        value: radioValue
      },
      stopPropagation: event.stopPropagation,
      preventDefault: event.preventDefault,
      nativeEvent: event
    }

    setIsChecked(!isChecked)
    if (inGroup) updateState?.(radioValue)
    onChange?.(customEvent)
  }

  return (
    <Label disabled={isDisabled}>
      {hasLabel && labelPosition === 'left' && (
        <RadioButtonText size={size} className='radiobutton-text'>
          {label}
        </RadioButtonText>
      )}
      <RadioButtonContainer
        className='radiobutton-container'
        checked={isChecked}
        disabled={isDisabled}
        tabIndex={isDisabled ? -1 : 0}
        size={size}
      >
        <VisuallyHidden
          as='input'
          type='radio'
          tabIndex={-1}
          value={radioValue}
          disabled={isDisabled}
          checked={isChecked}
          invalid={invalid}
          aria-checked={isChecked}
          aria-disabled={isDisabled}
          aria-invalid={invalid}
          onChange={handleOnChange}
          className='radiobutton-input'
          {...rest}
        />
        <StyledDot />
      </RadioButtonContainer>
      {hasLabel && labelPosition === 'right' && (
        <RadioButtonText size={size} className='radiobutton-text'>
          {label}
        </RadioButtonText>
      )}
    </Label>
  )
}

if (__DEV__) {
  RadioButton.displayName = 'RadioButton'
}

const RadioButtonContainer = styled.div`
  --mote-radio-button-size: ${({ size }) => SIZES[size].radio}px;

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--mote-radio-button-size);
  height: var(--mote-radio-button-size);
  min-height: var(--mote-radio-button-size);
  min-width: var(--mote-radio-button-size);
  padding: 0;
  border-radius: 100%;
  flex-shrink: 0;
  box-sizing: border-box;
  transition-property: opacity;
  transition-duration: 150ms;
  transition-timing-function: ease-in;

  ${({ disabled }) =>
    disabled
      ? css`
          opacity: 0.4;
          cursor: not-allowed;
        `
      : css`
          opacity: 1;
          cursor: pointer;
        `}

  ${({ checked, size }) =>
    checked
      ? css`
          span {
            border-width: ${SIZES[size].radio * 0.34}px;
            border-color: #ac0ae8;
          }

          &:hover {
            span {
              border-color: #9b09d1;
            }
          }

          &:active {
            span {
              border-color: #7807a2;
            }
          }
        `
      : css`
          &:hover {
            span {
              border-color: #ac0ae8;
            }
          }
        `};

  ${({ checked, disabled }) =>
    checked &&
    disabled &&
    css`
      border-color: #25252d;
      background-color: #25252d;

      &:hover,
      &:active {
        border-color: #25252d;
        background-color: #25252d;
      }
    `};
`

const Label = styled.label`
  display: inline-flex;
  justify-content: flex-start;
  vertical-align: top;
  align-items: center;
  position: relative;
  width: auto;
  gap: 8px;
  transition: all 150ms ease;

  ${({ disabled }) =>
    disabled
      ? css`
          opacity: 0.75;
          cursor: not-allowed;

          span.radiobutton-text {
            pointer-events: none;
          }
        `
      : css`
          opacity: 1;
        `};
`

const RadioButtonText = styled.span`
  display: inline-block;
  font-size: ${({ size }) => SIZES[size].font}px;
  line-height: 120%;
  font-style: normal;
  font-weight: 400;
  color: #25252d;
  user-select: none;
`

const StyledDot = styled.span`
  display: block;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d1d1e0;
  border-radius: 100%;
  background: #ffffff;
  transition-duration: 150ms;
  transition-timing-function: ease-in;
  transition-property: border-width, border-color;
`
